import * as React from "react";
import Grid from "@mui/material/Grid";
import Item from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import { Box, Button } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PhoneNumberField from "../../helper/Form/PhoneNumber";

// Define Yup schema for validation
const schema = yup.object().shape({
  volunteerName: yup
    .string()
    .required("Volunteer Name is required")
    .matches(
      /^[A-Za-z][A-Za-z.\s]*$/,
      "Must start with a letter and only contain letters, dots, and spaces"
    )
    .max(100, " cannot exceed 100 characters")
    .test(
      "not-all-same-characters",
      "Name cannot have all characters the same",
      (value) => {
        if (!value) return true; // skip if empty, 'required' already handles this
        const lettersOnly = value.replace(/[^A-Za-z]/g, "").toLowerCase();
        return !/^([a-z])\1+$/.test(lettersOnly); // blocks ffff, FFFF, etc.
      }
    ),
  volunteerEmail: yup
    .string()
    .required("Volunteer Email is required")
    .test(
      "no-spaces",
      "Email must not contain spaces",
      (value) => !/\s/.test(value || "")
    )
    .test(
      "valid-local-part",
      "Invalid email format: special characters cannot start/end or repeat in local part",
      (value) => {
        if (!value) return false;
        const [localPart] = value.split("@");
        // Disallow starting/ending with . _ -
        if (/^[._-]|[._-]$/.test(localPart)) return false;
        // Disallow consecutive special chars
        if (/([._-])\1+/.test(localPart)) return false;
        return true;
      }
    )
    .test("no-numeric-start", "Email cannot start with numbers", (value) => {
      if (!value) return false;
      const [localPart] = value.split("@");
      return !/^\d/.test(localPart);
    })
    .email("Invalid email"),
  volunteerPhone: yup
    .string()
    .required("Volunteer Phone is required")
    .matches(
      /^\+1 \(\d{3}\) \d{3}-\d{4}$/, // Matches "+1 (XXX) XXX-XXXX"
      "Phone number must be in the format +1 (XXX) XXX-XXXX"
    )
    .test(
      "is-ten-digits",
      "Phone number must contain exactly 10 digits",
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, ""); // Remove all non-digit characters
        return digits.length === 11 && digits.startsWith("1"); // Ensure it has country code
      }
    ),
});

export default function Step4({
  formData,
  setFormData,
  handleNext,
  handleBack,
  isVolunteerReadOnly,
}) {
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: formData,
  });

  React.useEffect(() => {
    if (formData.volunteerPhone) {
      setValue("volunteerPhone", formData.volunteerPhone, { shouldValidate: true });
    }
  }, [formData.volunteerPhone, setValue]);

  const onSubmit = (data) => {
    setFormData((prevData) => ({
      ...prevData,
      ...data,
    }));
    handleNext();
  };

  React.useEffect(() => {
    reset({
      volunteerName: formData?.volunteerName || "",
      volunteerPhone: formData?.volunteerPhone || "",
      volunteerEmail: formData?.volunteerEmail || "",
    });
  }, [formData, reset]);

  return (
    <>
      <Grid container spacing={2} p={2} sx={{ display: "flex" }}>
        <Grid item md={3} sm={12}>
          <Item>
            <Controller
              name="volunteerName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="outlined-textarea"
                  label={
                    <label>
                      Referral Name{" "}
                      <span style={{ color: "orangered" }}> *</span>
                    </label>
                  }
                  placeholder="Enter Referral Name"
                  size="small"
                  className="responsive-form"
                  error={!!error}
                  helperText={error ? error.message : ""}
                  sx={{ width: 280, maxWidth: "100%" }}
                  InputProps={{
                    readOnly: isVolunteerReadOnly,
                    sx: isVolunteerReadOnly ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {},
                  }}
                />
              )}
            />
          </Item>
        </Grid>
        <Grid item md={3} sm={12}>
          <Item>
            <Controller
              name="volunteerEmail"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="outlined-textarea"
                  label={
                    <label>
                      Referral Email{" "}
                      <span style={{ color: "orangered" }}> *</span>{" "}
                    </label>
                  }
                  placeholder="eg:abc123@example.com"
                  size="small"
                  className="responsive-form"
                  error={!!error}
                  helperText={error ? error.message : ""}
                  sx={{ width: 300, maxWidth: "100%" }}
                  InputProps={{
                    readOnly: isVolunteerReadOnly,
                    sx: isVolunteerReadOnly ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {},
                  }}
                />
              )}
            />
          </Item>
        </Grid>
        <Grid item md={3} sm={12}>
          <PhoneNumberField
            control={control}
            name="volunteerPhone"
            label={
              <label>
                Referral Mobile Number
                <span style={{ color: "orangered" }}>*</span>
              </label>
            }
            error={errors.volunteerPhone}
            disabled={isVolunteerReadOnly}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: "flex" }}>
        <Button variant="outlined" onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          sx={{ mt: 1, mr: 1 }}
        >
          Continue
        </Button>
      </Box>
    </>
  );
}
